import type {
  CableId,
  GlobalPerms,
  GlobalRoleType,
  KeysOf,
  LoggerId,
  OrganizationId,
  OrganizationPerms,
  OrganizationRoleType,
  ProjectId,
  ProjectPerms,
  ProjectRoleType,
  SiteId,
  UserId,
  UserWithPermissions,
} from '@beaded/models'
import { ApiKeyType } from '@beaded/models'

export enum category {
  CABLE = 'CABLE',
  CABLE_PRICES = 'CABLE_PRICES',
  CHALLENGE = 'CHALLENGE',
  CLOUDLOOP = 'CLOUDLOOP',
  ERPNEXT = 'ERPNEXT',
  GLOBAL = 'GLOBAL',
  LOGGER = 'LOGGER',
  LOGGER_ACTIVATION = 'LOGGER_ACTIVATION',
  MODEM = 'MODEM',
  NOTIFICATION = 'NOTIFICATION',
  NOTE = 'NOTE',
  ORDER = 'ORDER',
  ORGANIZATION = 'ORGANIZATION',
  PERMISSION = 'PERMISSION',
  PRODUCT_SERVICE = 'PRODUCT_SERVICE',
  PROJECT = 'PROJECT',
  READINGS = 'READINGS',
  SEARCH = 'SEARCH',
  SENSOR = 'SENSOR',
  SERVICE_CONTRACT = 'SERVICE_CONTRACT',
  SITE = 'SITE',
  USER = 'USER',
  SERVICE = 'SERVICE',
  SERVICE_ITEM = 'SERVICE_ITEM',
}

export enum perms {
  CREATE_LOGGER = 'CREATE_LOGGER',
  VIEW_LOGGER = 'VIEW_LOGGER',
  LIST_LOGGERS = 'LIST_LOGGERS',
  EDIT_LOGGER = 'EDIT_LOGGER',
  REMOVE_LOGGER = 'REMOVE_LOGGER',
  SUSPEND_LOGGER = 'SUSPEND_LOGGER',
  VALIDATE_LOGGER = 'VALIDATE_LOGGER',
  SEND_SBD_TO_LOGGER = 'SEND_SBD_TO_LOGGER',
  CREATE_LOGGER_EVENT = 'CREATE_LOGGER_EVENT',
  CAPTURE_LOGGER_DIAGNOSTIC_EVENT = 'CAPTURE_LOGGER_DIAGNOSTIC_EVENT',
  CREATE_LOGGER_HISTORY_ENTRY = 'CREATE_LOGGER_HISTORY_ENTRY',
  BULK_EDIT_LOGGER_HISTORY_ENTRY = 'BULK_EDIT_LOGGER_HISTORY_ENTRY',
  EDIT_LOGGER_HISTORY_ENTRY = 'EDIT_LOGGER_HISTORY_ENTRY',
  REMOVE_LOGGER_HISTORY_ENTRY = 'REMOVE_LOGGER_HISTORY_ENTRY',
  LOGGER_FIRMWARE_LIST_FILES = 'LOGGER_FIRMWARE_LIST_FILES',
  LOGGER_FIRMWARE_PUBLISH = 'LOGGER_FIRMWARE_PUBLISH',

  CREATE_PRODUCT_SERVICE = 'CREATE_PRODUCT_SERVICE',
  VIEW_PRODUCT_SERVICE = 'VIEW_PRODUCT_SERVICE',
  LIST_PRODUCT_SERVICES = 'LIST_PRODUCT_SERVICES',
  EDIT_PRODUCT_SERVICE = 'EDIT_PRODUCT_SERVICE',
  REMOVE_PRODUCT_SERVICE = 'REMOVE_PRODUCT_SERVICE',

  CREATE_SERVICE_CONTRACT = 'CREATE_SERVICE_CONTRACT',
  VIEW_SERVICE_CONTRACT = 'VIEW_SERVICE_CONTRACT',
  LIST_SERVICE_CONTRACTS = 'LIST_SERVICE_CONTRACTS',
  EDIT_SERVICE_CONTRACT = 'EDIT_SERVICE_CONTRACT',
  REMOVE_SERVICE_CONTRACT = 'REMOVE_SERVICE_CONTRACT',

  CREATE_SERVICE_CONTRACT_PAYMENT = 'CREATE_SERVICE_CONTRACT_PAYMENT',

  CREATE_SERVICE_ITEM = 'CREATE_SERVICE_ITEM',
  VIEW_SERVICE_ITEM = 'VIEW_SERVICE_ITEM',
  LIST_SERVICE_ITEMS = 'LIST_SERVICE_ITEMS',
  EDIT_SERVICE_ITEM = 'EDIT_SERVICE_ITEM',

  LIST_MODEMS = 'LIST_MODEMS',
  VIEW_MODEM = 'VIEW_MODEM',
  CREATE_MODEM = 'CREATE_MODEM',
  EDIT_MODEM = 'EDIT_MODEM',
  REMOVE_MODEM = 'REMOVE_MODEM',
  VALIDATE_MODEM = 'VALIDATE_MODEM',
  VIEW_MODEM_HISTORY_ENTRY = 'VIEW_MODEM_HISTORY_ENTRY',
  CREATE_MODEM_HISTORY_ENTRY = 'CREATE_MODEM_HISTORY_ENTRY',

  CREATE_ORGANIZATION = 'CREATE_ORGANIZATION',
  VIEW_ORGANIZATION = 'VIEW_ORGANIZATION',
  VIEW_ORGANIZATION_NAME = 'VIEW_ORGANIZATION_NAME',
  LIST_ORGANIZATIONS = 'LIST_ORGANIZATIONS',
  EDIT_ORGANIZATION = 'EDIT_ORGANIZATION',
  REMOVE_ORGANIZATION = 'REMOVE_ORGANIZATION',

  CREATE_PROJECT = 'CREATE_PROJECT',
  VIEW_PROJECT = 'VIEW_PROJECT',
  LIST_PROJECTS = 'LIST_PROJECTS',
  EDIT_PROJECT = 'EDIT_PROJECT',
  REMOVE_PROJECT = 'REMOVE_PROJECT',

  CREATE_NOTE = 'CREATE_NOTE',
  VIEW_NOTE = 'VIEW_NOTE',
  LIST_NOTES = 'LIST_NOTES',
  EDIT_NOTE = 'EDIT_NOTE',
  REMOVE_NOTE = 'REMOVE_NOTE',

  CREATE_NOTIFICATION = 'CREATE_NOTIFICATION',
  VIEW_NOTIFICATION = 'VIEW_NOTIFICATION',
  LIST_NOTIFICATIONS = 'LIST_NOTIFICATIONS',
  EDIT_NOTIFICATION = 'EDIT_NOTIFICATION',
  REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION',

  CREATE_CHALLENGE = 'CREATE_CHALLENGE',
  VIEW_CHALLENGE = 'VIEW_CHALLENGE',
  LIST_CHALLENGES = 'LIST_CHALLENGES',
  EDIT_CHALLENGE = 'EDIT_CHALLENGE',
  REMOVE_CHALLENGE = 'REMOVE_CHALLENGE',

  CREATE_CABLE = 'CREATE_CABLE',
  VIEW_CABLE = 'VIEW_CABLE',
  LIST_CABLES = 'LIST_CABLES',
  EDIT_CABLE = 'EDIT_CABLE',
  REMOVE_CABLE = 'REMOVE_CABLE',
  EDIT_LOCKED_CABLE = 'EDIT_LOCKED_CABLE',

  CREATE_SITE = 'CREATE_SITE',
  VIEW_SITE = 'VIEW_SITE',
  LIST_SITES = 'LIST_SITES',
  LIST_ALL_SITES = 'LIST_ALL_SITES',
  EDIT_SITE = 'EDIT_SITE',
  /** Rename the channels (sensors) of a site on the borehole dashboard (#4664). */
  EDIT_SENSOR_LABEL = 'EDIT_SENSOR_LABEL',
  REMOVE_SITE = 'REMOVE_SITE',
  BULK_CREATE_SITE = 'BULK_CREATE_SITE',

  CREATE_USER = 'CREATE_USER',

  LIST_PROJECT_USERS = 'LIST_PROJECT_USERS',

  LIST_ORGANIZATION_USERS = 'LIST_ORGANIZATION_USERS',

  LIST_GLOBAL_USERS = 'LIST_GLOBAL_USERS',

  VIEW_USER = 'VIEW_USER',
  LIST_USERS = 'LIST_USERS',
  EDIT_USER = 'EDIT_USER',
  REMOVE_USER = 'REMOVE_USER',
  ASSUME_USER_IDENTITY = 'ASSUME_USER_IDENTITY',
  VIEW_AS_USER = 'VIEW_AS_USER',

  MOVE_LOGGER_TO_PROJECT = 'MOVE_LOGGER_TO_PROJECT',
  MOVE_LOGGER_FROM_PROJECT = 'MOVE_LOGGER_FROM_PROJECT',

  MOVE_LOGGER_TO_ORGANIZATION = 'MOVE_LOGGER_TO_ORGANIZATION',
  MOVE_LOGGER_FROM_ORGANIZATION = 'MOVE_LOGGER_FROM_ORGANIZATION',

  MOVE_CABLE_TO_PROJECT = 'MOVE_CABLE_TO_PROJECT',
  MOVE_CABLE_FROM_PROJECT = 'MOVE_CABLE_FROM_PROJECT',

  MOVE_CABLE_TO_ORGANIZATION = 'MOVE_CABLE_TO_ORGANIZATION',
  MOVE_CABLE_FROM_ORGANIZATION = 'MOVE_CABLE_FROM_ORGANIZATION',

  MOVE_SITE_TO_PROJECT = 'MOVE_SITE_TO_PROJECT',
  MOVE_SITE_FROM_PROJECT = 'MOVE_SITE_FROM_PROJECT',

  MOVE_PROJECT_TO_ORGANIZATION = 'MOVE_PROJECT_TO_ORGANIZATION',
  MOVE_PROJECT_FROM_ORGANIZATION = 'MOVE_PROJECT_FROM_ORGANIZATION',

  ADD_SHARED_PROJECT_TO_ORGANIZATION = 'ADD_SHARED_PROJECT_TO_ORGANIZATION',

  READ_CABLE_PRICE = 'READ_CABLE_PRICE',
  LIST_CABLE_PRICES = 'LIST_CABLE_PRICES',
  CREATE_CABLE_PRICES = 'CREATE_CABLE_PRICES',

  CREATE_CABLE_EVENT = 'CREATE_CABLE_EVENT',
  BULK_EDIT_CABLE_HISTORY_ENTRY = 'BULK_EDIT_CABLE_HISTORY_ENTRY',
  CREATE_CABLE_HISTORY_ENTRY = 'CREATE_CABLE_HISTORY_ENTRY',
  EDIT_CABLE_HISTORY_ENTRY = 'EDIT_CABLE_HISTORY_ENTRY',
  REMOVE_CABLE_HISTORY_ENTRY = 'REMOVE_CABLE_HISTORY_ENTRY',

  DOWNLOAD_FILE_CABLE_CUTTER = 'DOWNLOAD_FILE_CABLE_CUTTER',
  DOWNLOAD_FILE_FISHBOWL = 'DOWNLOAD_FILE_FISHBOWL',
  SYNC_CABLE_ERP_NEXT = 'SYNC_CABLE_ERP_NEXT',

  CLOUDLOOP_ACTIVATE_SUBSCRIBER = 'CLOUDLOOP_ACTIVATE_SUBSCRIBER',
  CLOUDLOOP_CREATE_DESTINATION = 'CLOUDLOOP_CREATE_DESTINATION',
  CLOUDLOOP_CREATE_HARDWARE = 'CLOUDLOOP_CREATE_HARDWARE',
  CLOUDLOOP_CREATE_SUBSCRIBER = 'CLOUDLOOP_CREATE_SUBSCRIBER',
  CLOUDLOOP_DEACTIVATE_SUBSCRIBER = 'CLOUDLOOP_DEACTIVATE_SUBSCRIBER',
  CLOUDLOOP_GET_HARDWARE = 'CLOUDLOOP_GET_HARDWARE',
  CLOUDLOOP_GET_SUBSCRIBER = 'CLOUDLOOP_GET_SUBSCRIBER',
  CLOUDLOOP_LIST_SUBSCRIBERS = 'CLOUDLOOP_LIST_SUBSCRIBERS',
  CLOUDLOOP_GET_USAGE_SUMMARY = 'CLOUDLOOP_GET_USAGE_SUMMARY',
  CLOUDLOOP_REASSOCIATE_SUBSCRIBER = 'CLOUDLOOP_REASSOCIATE_SUBSCRIBER',
  CLOUDLOOP_RESUME_SUBSCRIBER = 'CLOUDLOOP_RESUME_SUBSCRIBER',
  CLOUDLOOP_SEND_MESSAGE = 'CLOUDLOOP_SEND_MESSAGE',
  CLOUDLOOP_SUSPEND_SUBSCRIBER = 'CLOUDLOOP_SUSPEND_SUBSCRIBER',
  CLOUDLOOP_UPDATE_SUBSCRIBER = 'CLOUDLOOP_UPDATE_SUBSCRIBER',
  CLOUDLOOP_SETUP_WIZARD = 'CLOUDLOOP_SETUP_WIZARD',

  CREATE_SENSOR = 'CREATE_SENSOR',
  VIEW_SENSOR = 'VIEW_SENSOR',
  LIST_SENSORS = 'LIST_SENSORS',
  EDIT_SENSOR = 'EDIT_SENSOR',
  REMOVE_SENSOR = 'REMOVE_SENSOR',
  CALIBRATE_SENSOR = 'CALIBRATE_SENSOR',
  REMOVE_SENSOR_CALIBRATION = 'REMOVE_SENSOR_CALIBRATION',
  VIEW_SENSOR_CALIBRATIONS_FOR_SITE = 'VIEW_SENSOR_CALIBRATIONS_FOR_SITE',
  VIEW_SENSOR_CALIBRATIONS_FOR_CABLE = 'VIEW_SENSOR_CALIBRATIONS_FOR_CABLE',

  SEARCH = 'SEARCH',
  SEARCH_EXTENSION_CONTEXT = 'SEARCH_EXTENSION_CONTEXT',

  CREATE_READING = 'CREATE_READING',
  LIST_READINGS = 'LIST_READINGS',
  VIEW_READING = 'VIEW_READING',
  REPROCESS_READINGS = 'REPROCESS_READINGS',
  EDIT_READING = 'EDIT_READING',
  INGEST_READINGS = 'INGEST_READINGS',

  CAN_ASSIGN_GLOBAL_ADMIN = 'CAN_ASSIGN_GLOBAL_ADMIN',
  CAN_ASSIGN_GLOBAL_EDIT = 'CAN_ASSIGN_GLOBAL_EDIT',
  CAN_ASSIGN_GLOBAL_VIEW = 'CAN_ASSIGN_GLOBAL_VIEW',

  CAN_ASSIGN_LOGGER_ADMIN = 'CAN_ASSIGN_LOGGER_ADMIN',
  CAN_ASSIGN_CABLE_ADMIN = 'CAN_ASSIGN_CABLE_ADMIN',
  CAN_ASSIGN_SALES_ADMIN = 'CAN_ASSIGN_SALES_ADMIN',

  CAN_ASSIGN_ORGANIZATION_ADMIN = 'CAN_ASSIGN_ORGANIZATION_ADMIN',
  // Paid per-site forecasting (#3982, spec 5.1 / Q11): buy it, and open the
  // organisation's Stripe Customer Portal. ORGANIZATION_ADMIN only.
  PURCHASE_FORECASTING = 'PURCHASE_FORECASTING',
  CAN_ASSIGN_ORGANIZATION_EDIT = 'CAN_ASSIGN_ORGANIZATION_EDIT',
  CAN_ASSIGN_ORGANIZATION_VIEW = 'CAN_ASSIGN_ORGANIZATION_VIEW',

  CAN_ASSIGN_PROJECT_ADMIN = 'CAN_ASSIGN_PROJECT_ADMIN',
  CAN_ASSIGN_PROJECT_EDIT = 'CAN_ASSIGN_PROJECT_EDIT',
  CAN_ASSIGN_PROJECT_VIEW = 'CAN_ASSIGN_PROJECT_VIEW',

  LIST_USER_PERMISSIONS = 'LIST_USER_PERMISSIONS',

  LOCK_CABLE = 'LOCK_CABLE',
  CABLE_RECONCILIATION = 'CABLE_RECONCILIATION',

  ERPNEXT_WEBHOOK = 'ERPNEXT_WEBHOOK',
  ADD_CONTACT_TO_ERPNEXT = 'ADD_CONTACT_TO_ERPNEXT',

  CREATE_ORDER = 'CREATE_ORDER',
  LIST_ORDERS = 'LIST_ORDERS',
  VIEW_ORDER = 'VIEW_ORDER',
  CAN_REPLACE_ORDER_IN_CABLE = 'CAN_REPLACE_ORDER_IN_CABLE',
  EDIT_ORDER = 'EDIT_ORDER',
  RESYNC_ORDER = 'RESYNC_ORDER',

  LIST_LOGGER_ACTIVATIONS = 'LIST_LOGGER_ACTIVATIONS',
  VIEW_LOGGER_ACTIVATION = 'VIEW_LOGGER_ACTIVATION',
  EDIT_LOGGER_ACTIVATION = 'EDIT_LOGGER_ACTIVATION',
  CREATE_LOGGER_ACTIVATION = 'CREATE_LOGGER_ACTIVATION',
}

const globalAdminExcludedPermissions = [
  perms.CAPTURE_LOGGER_DIAGNOSTIC_EVENT,
  perms.CREATE_READING,
  perms.EDIT_READING,
  perms.INGEST_READINGS,
  perms.ERPNEXT_WEBHOOK,
  perms.SEARCH_EXTENSION_CONTEXT,
  // Staff never pay for a customer (#3620 decision 6, spec 5.1); spread into
  // globalEditExcludedPermissions below, so no global role holds it.
  perms.PURCHASE_FORECASTING,
]

const globalEditExcludedPermissions = [
  ...globalAdminExcludedPermissions,
  perms.REMOVE_LOGGER,
  perms.REMOVE_MODEM,
  perms.EDIT_LOCKED_CABLE,
  perms.ADD_SHARED_PROJECT_TO_ORGANIZATION,
  perms.LOGGER_FIRMWARE_LIST_FILES,
  perms.LOGGER_FIRMWARE_PUBLISH,
  perms.CREATE_CABLE_PRICES,
  perms.CAN_ASSIGN_GLOBAL_ADMIN,
  perms.CAN_ASSIGN_LOGGER_ADMIN,
  perms.CAN_ASSIGN_CABLE_ADMIN,
  perms.CAN_ASSIGN_SALES_ADMIN,
  perms.LOCK_CABLE,
  perms.CABLE_RECONCILIATION,
  perms.RESYNC_ORDER,
  // Impersonation is GLOBAL_ADMIN only (#4406): a GLOBAL_EDIT holder who
  // could switch into a GLOBAL_ADMIN would acquire every admin permission.
  perms.ASSUME_USER_IDENTITY,
]

type RoleType = ProjectRoleType | OrganizationRoleType | GlobalRoleType

export const CAPTURE_APP_PERMISSIONS: Array<KeysOf<typeof perms>> = [
  perms.CAPTURE_LOGGER_DIAGNOSTIC_EVENT,
  perms.CLOUDLOOP_GET_SUBSCRIBER,
  perms.INGEST_READINGS,
]

export const SDM_APP_PERMISSIONS: Array<KeysOf<typeof perms>> = [
  perms.LIST_CABLES,
  perms.LIST_SITES,
  perms.LIST_ALL_SITES,
  perms.LIST_READINGS,
  perms.CREATE_READING,
]

export const ERPNEXT_PERMISSIONS: Array<KeysOf<typeof perms>> = [
  perms.ERPNEXT_WEBHOOK,
]

export const ETHERNET_ACTIVATOR_PERMISSIONS: Array<KeysOf<typeof perms>> = [
  perms.CREATE_READING,
]

export const CHROME_EXTENSION_PERMISSIONS: Array<KeysOf<typeof perms>> = [
  perms.SEARCH_EXTENSION_CONTEXT,
  perms.ADD_CONTACT_TO_ERPNEXT,
]

const GLOBAL_ADMIN = [
  ...Object.values(perms).filter(
    (p) => !globalAdminExcludedPermissions.includes(p),
  ),
]

const GLOBAL_EDIT = [
  ...Object.values(perms).filter(
    (p) => !globalEditExcludedPermissions.includes(p),
  ),
]

const GLOBAL_VIEW = [
  perms.VIEW_ORGANIZATION,
  perms.LIST_ORGANIZATIONS,
  perms.VIEW_PROJECT,
  perms.LIST_PROJECTS,
  perms.VIEW_SITE,
  perms.LIST_SITES,
  perms.VIEW_CABLE,
  perms.LIST_CABLES,
  perms.VIEW_LOGGER,
  perms.LIST_LOGGERS,
  perms.LIST_ORGANIZATION_USERS,
  perms.LIST_PROJECT_USERS,
  perms.LIST_MODEMS,
  perms.VIEW_MODEM,
  perms.VIEW_SENSOR_CALIBRATIONS_FOR_SITE,
  perms.SEARCH,
  perms.VIEW_MODEM_HISTORY_ENTRY,
  perms.VIEW_USER,
  perms.CLOUDLOOP_GET_HARDWARE,
  perms.CLOUDLOOP_GET_SUBSCRIBER,
  perms.CLOUDLOOP_LIST_SUBSCRIBERS,
  perms.CLOUDLOOP_GET_USAGE_SUMMARY,
  perms.LIST_USERS,
  perms.READ_CABLE_PRICE,
  perms.LIST_CABLE_PRICES,
  perms.VIEW_READING,
  perms.LIST_READINGS,
  perms.LIST_GLOBAL_USERS,
  perms.LIST_USER_PERMISSIONS,
  perms.VIEW_ORGANIZATION_NAME,
  perms.VIEW_PRODUCT_SERVICE,
  perms.LIST_PRODUCT_SERVICES,
  perms.VIEW_SERVICE_CONTRACT,
  perms.LIST_SERVICE_CONTRACTS,
  perms.LIST_ORDERS,
  perms.VIEW_ORDER,
  perms.LIST_SERVICE_ITEMS,
  perms.VIEW_SERVICE_ITEM,
  perms.LIST_NOTIFICATIONS,
  perms.VIEW_NOTIFICATION,
  perms.LIST_CHALLENGES,
  perms.VIEW_CHALLENGE,
  perms.LIST_NOTES,
  perms.VIEW_NOTE,
]

const CABLE_ADMIN = [
  perms.EDIT_LOCKED_CABLE,
  perms.CREATE_SENSOR,
  perms.VIEW_SENSOR,
  perms.LIST_SENSORS,
  perms.EDIT_SENSOR,
  perms.CALIBRATE_SENSOR,
  perms.REMOVE_SENSOR,
  perms.REMOVE_SENSOR_CALIBRATION,
  perms.CAN_ASSIGN_CABLE_ADMIN,
  perms.LOCK_CABLE,
  perms.CABLE_RECONCILIATION,
  perms.RESYNC_ORDER,
]

const LOGGER_ADMIN = [
  perms.LOGGER_FIRMWARE_LIST_FILES,
  perms.LOGGER_FIRMWARE_PUBLISH,
  perms.REMOVE_LOGGER,
  perms.REMOVE_MODEM,
  perms.CAN_ASSIGN_LOGGER_ADMIN,
]

const SALES_ADMIN = [
  perms.LIST_CABLE_PRICES,
  perms.CREATE_CABLE_PRICES,
  perms.CAN_ASSIGN_SALES_ADMIN,
]

const PROJECT_VIEW = [
  perms.LIST_CHALLENGES,
  perms.VIEW_CHALLENGE,
  perms.VIEW_ORGANIZATION_NAME,
  perms.VIEW_PROJECT,
  perms.LIST_PROJECTS,
  perms.VIEW_SITE,
  perms.LIST_SITES,
  perms.VIEW_CABLE,
  perms.LIST_CABLES,
  perms.LIST_LOGGERS,
  perms.VIEW_SENSOR_CALIBRATIONS_FOR_SITE,
  // VIEW_USER / EDIT_USER are deliberately NOT here (#4396): an org/project
  // role says nothing about which USER may be acted on. Self-service comes
  // from hasPermission's targetUserId branch; managing another user needs a
  // global role or organization user-management rights over the target
  // (apps/api/src/ez/endpoints/user.ts, userTargetAuthorization).
  perms.READ_CABLE_PRICE,
  perms.LIST_READINGS,
  perms.VIEW_LOGGER,
]

const PROJECT_EDIT = [
  ...PROJECT_VIEW,
  perms.EDIT_PROJECT,
  perms.CREATE_SITE,
  perms.EDIT_SITE,
  perms.REMOVE_SITE,
  perms.LIST_PROJECT_USERS,
  perms.CAN_ASSIGN_PROJECT_VIEW,
  perms.EDIT_LOGGER_ACTIVATION,
  perms.CREATE_LOGGER_ACTIVATION,
  perms.VIEW_LOGGER_ACTIVATION,
  perms.EDIT_CABLE,
  perms.CREATE_CABLE_HISTORY_ENTRY,
  perms.EDIT_CABLE_HISTORY_ENTRY,
]

const PROJECT_ADMIN = [
  ...PROJECT_EDIT,
  perms.EDIT_SENSOR_LABEL,
  perms.REMOVE_SITE,
  perms.CAN_ASSIGN_PROJECT_ADMIN,
  perms.CAN_ASSIGN_PROJECT_EDIT,
]

const ORGANIZATION_VIEW = [...PROJECT_VIEW, perms.VIEW_ORGANIZATION]

const ORGANIZATION_EDIT = [
  ...ORGANIZATION_VIEW,
  ...PROJECT_EDIT,
  perms.EDIT_ORGANIZATION,
  perms.CREATE_PROJECT,
  // perms.CAN_ASSIGN_ORGANIZATION_VIEW,
  // perms.CAN_ASSIGN_ORGANIZATION_EDIT,
  perms.CAN_ASSIGN_PROJECT_EDIT,
]

const ORGANIZATION_ADMIN = [
  ...ORGANIZATION_EDIT,
  ...PROJECT_ADMIN,
  perms.CREATE_PROJECT,
  perms.REMOVE_PROJECT,
  perms.LIST_ORGANIZATION_USERS,
  perms.CAN_ASSIGN_ORGANIZATION_VIEW,
  perms.CAN_ASSIGN_ORGANIZATION_EDIT,
  perms.CAN_ASSIGN_ORGANIZATION_ADMIN,
  perms.VIEW_ORGANIZATION_NAME,
  perms.PURCHASE_FORECASTING,
]

export const rolePermissions: {
  [key in RoleType]: Array<KeysOf<typeof perms>>
} = {
  GLOBAL_ADMIN,
  GLOBAL_EDIT,
  GLOBAL_VIEW,
  CABLE_ADMIN,
  LOGGER_ADMIN,
  SALES_ADMIN,
  PROJECT_VIEW,
  PROJECT_EDIT,
  PROJECT_ADMIN,
  ORGANIZATION_VIEW,
  ORGANIZATION_EDIT,
  ORGANIZATION_ADMIN,
}

export type HasPermissionContext = {
  /**
   * The type of the API key that authenticated this request, if any (set
   * from request.session.apiKeyType). Grants the key type's fixed
   * permissions below. The key VALUE is never part of the context: the
   * context is logged on denial (#4411).
   */
  apiKeyType?: ApiKeyType
  id?: number
  organizationId?: OrganizationId
  projectId?: ProjectId
  siteId?: SiteId
  cableId?: CableId
  loggerId?: LoggerId
  userId?: UserId
  /**
   * The user a user-scoped route acts on (the `:id` path param or the
   * `userId` input of `/user` routes), resolved server-side by the route's
   * enricher with `'me'` mapped to the caller. Distinct from `userId`, which
   * is always the CALLER. The VIEW_USER / EDIT_USER self-service grant below
   * applies only when this equals the caller's id (#4396).
   */
  targetUserId?: UserId
  cableSerial?: number
  loggerSerial?: string
  serial?: string | number | { 'serial.model': string; 'serial.id': number }
  /**
   * Set by a server-side enricher when the target resource's scope could not
   * be resolved (e.g. a site with no ACTIVE project/org link). The
   * "any org/project role anywhere" fallbacks below are then skipped, so only
   * global roles (and context-free grants such as API-key types) apply.
   * Without this, an unresolvable site would be readable by any user holding
   * any role anywhere (#3923).
   */
  scopeUnresolved?: boolean
}

export const roleHasPermission =
  (action: keyof typeof perms) =>
  (p: GlobalPerms | OrganizationPerms | ProjectPerms) =>
    rolePermissions[p.role]?.includes(action)

const userHasOrganizationRole = (context: HasPermissionContext) => (o: any) =>
  o.role !== null && o.id === context.organizationId

const userHasAnyOrganizationRole =
  (context: HasPermissionContext) => (o: any) =>
    o.role !== null

const userHasProjectRole = (context: HasPermissionContext) => (p: any) =>
  p.role !== null &&
  (context.projectId !== undefined
    ? context.organizationId !== undefined
      ? p.id === context.projectId &&
        p.organizationId === context.organizationId
      : p.id === context.projectId
    : p.organizationId === context.organizationId)

const userHasAnyProjectRole = (context: HasPermissionContext) => (p: any) =>
  p.role !== null

export const hasPermission = (
  user: UserWithPermissions,
  action: keyof typeof perms,
  context: HasPermissionContext,
) => {
  if (user.permissions?.global?.some(roleHasPermission(action))) {
    return true
  }

  // console.log('no global perm', user, action, context)

  if (
    context?.organizationId !== undefined &&
    user.permissions?.organizations
      ?.filter(userHasOrganizationRole(context))
      ?.some(roleHasPermission(action))
  ) {
    return true
  }

  if (
    context?.organizationId !== undefined &&
    user.permissions?.projects
      ?.filter(userHasProjectRole(context))
      ?.some(roleHasPermission(action))
  )
    return true

  if (
    context?.organizationId === undefined &&
    context?.projectId === undefined &&
    !context?.scopeUnresolved &&
    user.permissions?.organizations
      ?.filter(userHasAnyOrganizationRole(context))
      ?.some(roleHasPermission(action))
  )
    return true

  if (
    context.projectId !== undefined &&
    user.permissions?.projects
      ?.filter(userHasProjectRole(context))
      ?.some(roleHasPermission(action))
  )
    return true

  if (
    context?.organizationId === undefined &&
    context.projectId === undefined &&
    !context?.scopeUnresolved &&
    user.permissions?.projects
      ?.filter(userHasAnyProjectRole(context))
      ?.some(roleHasPermission(action))
  )
    return true

  if (context.apiKeyType !== undefined) {
    const apiKeyType = context.apiKeyType

    if (
      apiKeyType === ApiKeyType.CAPTUREAPP &&
      CAPTURE_APP_PERMISSIONS.includes(action)
    )
      return true

    if (apiKeyType === ApiKeyType.SDM && SDM_APP_PERMISSIONS.includes(action)) {
      return true
    }

    if (
      apiKeyType === ApiKeyType.ETHERNET_ACTIVATOR &&
      ETHERNET_ACTIVATOR_PERMISSIONS.includes(action)
    ) {
      return true
    }

    if (
      apiKeyType === ApiKeyType.ERPNEXT &&
      ERPNEXT_PERMISSIONS.includes(action)
    ) {
      return true
    }

    if (
      apiKeyType === ApiKeyType.CHROME_EXTENSION &&
      CHROME_EXTENSION_PERMISSIONS.includes(action)
    ) {
      return true
    }
  }

  // Self-service: a user may view and edit THEMSELVES. This must compare the
  // route's TARGET user against the caller -- never `context.userId`, which
  // buildPermissionContext always sets to the caller, so comparing it to
  // `user.id` granted VIEW_USER / EDIT_USER on every target (#4396). With no
  // resolved target the grant does not apply (fail closed). The caller may be
  // authenticated by session or by API key; either way `user` is the caller.
  if (
    (action === perms.VIEW_USER || action === perms.EDIT_USER) &&
    context.targetUserId !== undefined &&
    context.targetUserId === user.id
  ) {
    return true
  }

  console.info(`no perm\n${user.id} : ${user.email}`, action, context)

  return false
}
